import { omit } from 'lodash';
import {
  assignProps,
  assignValue,
  compareByName,
  findSelectedInCategory,
  isValidFilter,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import { findWithUUID } from '@/utils/uuidHelper';
import withDraft from '@/utils/draftHelper';
import { userUsergroupsActions } from '../constants';
import { findUserGroups, findUserGroup, userGroupUsersGroupsState } from './state';

const addUserToUserGroup = (action, users) => {
  const user = { ...action.user, ...userGroupUsersGroupsState };
  users.results.push(user);
  return user;
};

const findUserInUserGroup = (draft, action) => {
  const { users } = findUserGroups(draft, action);
  return findWithUUID(users.results, action.user.uuid) || addUserToUserGroup(action, users);
};

const toggleAssignUsersToUserGroupSelected = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  if (!item.selected) {
    const selectedUserGroups = findSelectedInCategory(draft.userGroups, action.category);
    selectedUserGroups.forEach((userGroup) => assignValue(userGroup, 'selected', false));
  }
  item.selected = !item.selected;
});

const fetchUsersGroups = withDraft((draft, action) => {
  const user = findUserInUserGroup(draft, action);
  user.userGroups = {
    ...user.userGroups,
    ...omit(userGroupUsersGroupsState.userGroups, 'results'),
    readStatus: states.PENDING,
    readErrors: [],
  };
});

const fetchFilteredGroups = withDraft((draft, action) => {
  const { search } = findUserInUserGroup(draft, action).userGroups;
  assignProps(search, {
    filter: action.filter,
    filteredResults: [],
    readStatus: isValidFilter(action.filter, action) ? states.PENDING : states.NONE,
    readErrors: [],
  });
});

const updateUsersUsergroupProperties = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, action.properties);
});

const showFilteredGroups = withDraft((draft, action) => {
  const { search } = findUserInUserGroup(draft, action).userGroups;
  assignProps(search, { filteredResults: action.results, readStatus: states.SUCCESS, readErrors: [] });
});

const showFilteredGroupsError = withDraft((draft, action) => {
  const { search } = findUserInUserGroup(draft, action).userGroups;
  assignProps(search, { filteredResults: [], readStatus: states.ERROR, readErrors: [responseError(action)] });
});

const showUsersGroups = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, {
    readStatus: states.SUCCESS,
    readErrors: [],
    results: action.results,
    count: action.resultsCount,
    currentPage: action.currentPage,
    totalPages: action.totalPages,
  });
});

const showUsersGroupsError = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { readStatus: states.ERROR, readErrors: [responseError(action)], results: [] });
});

const removeUsersGroup = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  userGroups.results.forEach((group) => {
    if (group.code === action.group.code) {
      assignProps(group, { destroyStatus: states.PENDING, destroyErrors: [] });
    }
  });
});

const removeUsersGroupError = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  userGroups.results.forEach((group) => {
    if (group.code === action.group.code) {
      assignProps(group, { destroyStatus: states.ERROR, destroyErrors: [responseError(action)] });
    }
  });
});

const removeUsersGroupSuccess = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { results: userGroups.results.filter((ug) => ug.code !== action.group.code) });
});

const updateUsersGroupsToAdd = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { groupsToAdd: action.groups });
});

const addUsersGroups = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { createStatus: states.PENDING, createErrors: [] });
});

const addUsersGroupsSuccess = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  action.groups.forEach((group) => userGroups.results.push(group));
  assignProps(userGroups, {
    createStatus: states.SUCCESS,
    createErrors: [],
    groupsToAdd: [],
    results: userGroups.results.sort(compareByName),
  });
});

const addUsersGroupsLocal = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  action.groups.forEach((group) => userGroups.results.push(group));
  assignProps(userGroups, { groupsToAdd: [], results: userGroups.results.sort(compareByName) });
});

const addUsersGroupsError = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { createStatus: states.ERROR, createErrors: [action.errors[0].error] });
});

const dismissUsersGroupsError = withDraft((draft, action) => {
  const { userGroups } = findUserInUserGroup(draft, action);
  assignProps(userGroups, { readStatus: states.NONE, readErrors: [] });
});

export default {
  [userUsergroupsActions.ADD_USERS_GROUPS]: addUsersGroups,
  [userUsergroupsActions.ADD_USERS_GROUPS_ERROR]: addUsersGroupsError,
  [userUsergroupsActions.ADD_USERS_GROUPS_SUCCESS]: addUsersGroupsSuccess,
  [userUsergroupsActions.ADD_USERS_GROUPS_LOCAL]: addUsersGroupsLocal,
  [userUsergroupsActions.DISMISS_USERS_GROUPS_ERROR]: dismissUsersGroupsError,
  [userUsergroupsActions.FETCH_FILTERED_GROUPS]: fetchFilteredGroups,
  [userUsergroupsActions.FETCH_USERS_GROUPS]: fetchUsersGroups,
  [userUsergroupsActions.REMOVE_USERS_GROUP]: removeUsersGroup,
  [userUsergroupsActions.REMOVE_USERS_GROUP_ERROR]: removeUsersGroupError,
  [userUsergroupsActions.REMOVE_USERS_GROUP_SUCCESS]: removeUsersGroupSuccess,
  [userUsergroupsActions.REMOVE_USERS_GROUP_LOCAL]: removeUsersGroupSuccess,
  [userUsergroupsActions.SHOW_FILTERED_GROUPS]: showFilteredGroups,
  [userUsergroupsActions.SHOW_FILTERED_GROUPS_ERROR]: showFilteredGroupsError,
  [userUsergroupsActions.SHOW_USERS_GROUPS]: showUsersGroups,
  [userUsergroupsActions.SHOW_USERS_GROUPS_ERROR]: showUsersGroupsError,
  [userUsergroupsActions.TOGGLE_ASSIGN_USERS_TO_USER_GROUP_SELECTED]: toggleAssignUsersToUserGroupSelected,
  [userUsergroupsActions.UPDATE_USERS_GROUPS_TO_ADD]: updateUsersGroupsToAdd,
  [userUsergroupsActions.UPDATE_USERS_USERGROUP_PROPERTIES]: updateUsersUsergroupProperties,
};
